import React, { memo, useEffect, useState } from "react";
import { FormControl, TextField, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import useStableCallback from "../../hooks/useStableCallback";
import { setItemFilters, setPogFilters } from "../../store/globalSlice";

const StyledDatePicker = styled((props) => <TextField {...props} />)(
  ({ theme }) => ({
    "& .MuiInputBase-root": {
      borderRadius: 3,
      position: "relative",
      backgroundColor: theme.palette.mode === "light" ? "#00000029" : "#1A2027",
      border: "1px solid",
      borderColor: theme.palette.mode === "light" ? "#E0E3E7" : "#2D3843",
      fontSize: 11,
      width: "9rem",
      padding: "1px 3px",
      transition: theme.transitions.create([
        "border-color",
        "background-color",
        "box-shadow",
      ]),
      // Use the system font instead of the default Roboto font.
      fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(","),
      "&:focus-within": {
        boxShadow: `${alpha(theme.palette.primary.main, 0.15)} 0 0 0 0.1rem`,
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputBase-input": {
      padding: "0px 0px 0px 0px !important",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.6rem",
      fontWeight: 500,
      margin: "0px",
      height: "0px",
      width: "9rem",
    },
  })
);

const CustomDateRangePicker = ({
  isErrorPresent = () => {},
  isItemLevel = false,
  showInlineError = false,
}) => {
  const dispatch = useDispatch();
  const { startDate, endDate } = useSelector((store) =>
    !!isItemLevel ? store.global.itemFilters : store.global.pogFilters
  );
  const [errors, setErrors] = useState({
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    isErrorPresent(!!errors?.startDate || !!errors?.endDate ? true : false);
  }, [errors?.startDate, errors?.endDate, isErrorPresent]);

  useEffect(() => {
    if (!!startDate && !endDate) {
      setErrors({
        ...errors,
        startDate: "",
        endDate: "Please select End Date",
      });
    } else if (!!endDate && !startDate) {
      setErrors({
        ...errors,
        startDate: "Please select Start Date",
        endDate: "",
      });
    } else if (endDate < startDate) {
      setErrors({
        ...errors,
        startDate: "",
        endDate: "End Date should not be less than Start Date",
      });
    } else {
      setErrors({
        ...errors,
        startDate: "",
        endDate: "",
      });
    }
  }, [startDate, endDate]);

  const handleStartDate = useStableCallback((e) => {
    const newValue = e?.target?.value;
    if (!isItemLevel) {
      dispatch(setPogFilters({ field: "startDate", value: newValue }));
    } else {
      dispatch(setItemFilters({ field: "startDate", value: newValue }));
    }
  });

  const handleEndDate = useStableCallback((e) => {
    const newValue = e?.target?.value;
    if (!isItemLevel) {
      dispatch(setPogFilters({ field: "endDate", value: newValue }));
    } else {
      dispatch(setItemFilters({ field: "endDate", value: newValue }));
    }
  });

  return (
    <>
      <Typography variant="caption" fontWeight={500} lineHeight="unset" mr={1}>
        Start Date:
      </Typography>
      <FormControl>
        <StyledDatePicker
          margin="none"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={handleStartDate}
          helperText={errors?.startDate}
          error={!!errors?.startDate}
          FormHelperTextProps={{
            sx: {
              textWrap: showInlineError ? "nowrap" : "wrap",
            },
          }}
        />
      </FormControl>
      <Typography variant="caption" fontWeight={500} lineHeight="unset" mx={1}>
        End Date:
      </Typography>
      <StyledDatePicker
        margin="none"
        type="date"
        InputLabelProps={{ shrink: true }}
        value={endDate}
        onChange={handleEndDate}
        helperText={errors?.endDate}
        error={!!errors?.endDate}
        sx={{ mr: 1 }}
        FormHelperTextProps={{
          sx: {
            textWrap: showInlineError ? "nowrap" : "wrap",
          },
        }}
      />
    </>
  );
};

export default memo(CustomDateRangePicker);
